import { createColumnHelper } from '@tanstack/react-table';

import { IconButton } from '@yaradigitallabs/ahua-react';

import type { BulkImportHistoryItem } from '../types/bulkImportsHistory.types';

const columnHelper = createColumnHelper<BulkImportHistoryItem>();

type DownloadHandler = (row: BulkImportHistoryItem) => void;

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatUploadedDateTime = (dateInput: string) => {
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) {
    return dateInput;
  }

  const datePart = new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).format(date);

  const timePart = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone,
  }).format(date);

  return `${datePart} | ${timePart}`;
};

const StatusBadge = ({
  status,
}: {
  status: BulkImportHistoryItem['status'];
}) => {
  const badgeClasses =
    status === 'Complete'
      ? 'bg-green-100 text-green-800'
      : status === 'Partial Reject'
        ? 'bg-yellow-100 text-yellow-800'
        : status === 'Processing'
          ? 'bg-blue-100 text-blue-800'
          : 'bg-red-100 text-red-800';

  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${badgeClasses}`}
    >
      {status}
    </span>
  );
};

const renderResult = (item: BulkImportHistoryItem) => {
  if (item.inProgress) {
    return <span>In progress...</span>;
  }

  return (
    <span>{`${item.createdRows} created · ${item.failedRows} failed`}</span>
  );
};

export const createBulkImportsHistoryColumns = (
  onDownloadReport?: DownloadHandler
) => [
  columnHelper.accessor('fileName', {
    header: () => 'File',
    cell: info => <div className="w-64">{info.getValue()}</div>,
    size: 200,
  }),
  columnHelper.accessor('uploadedAt', {
    header: () => 'Uploaded',
    cell: info => <span>{formatUploadedDateTime(info.getValue())}</span>,
    size: 180,
  }),
  columnHelper.accessor('totalRows', {
    header: () => 'Total rows',
    cell: info => <span>{info.getValue()}</span>,
    size: 100,
  }),
  columnHelper.accessor('status', {
    header: () => 'Status',
    cell: info => <StatusBadge status={info.getValue()} />,
    size: 120,
  }),
  columnHelper.display({
    id: 'result',
    header: () => 'Result',
    cell: info => {
      if (info.row.original.status === 'Processing') {
        return null;
      }
      return renderResult(info.row.original);
    },
    size: 180,
  }),
  columnHelper.display({
    id: 'actions',
    header: () => 'Actions',
    cell: info => {
      const rowData = info.row.original;
      return (
        <IconButton
          icon="Download"
          size="xs"
          variant="ghost"
          title="Download CSV"
          onClick={() => onDownloadReport?.(rowData)}
          disabled={!rowData.errorFilePresent}
        />
      );
    },
    size: 80,
  }),
];
